import React from 'react'

import { List, ListItem } from '../../../../../../components/pdf/List'
import Separator from '../../../../../../components/pdf/Separator'
import Text from '../../../../../../components/pdf/Text'
import { HEADING_COLOR } from '../../_helpers/brand'

/**
 * Blocos de texto corrido reutilizados pelas seções institucionais do PGR Bradesco
 * (texto literal do modelo aprovado por Julia Buzzo — "Modelo PGR 2026 - Banco
 * Bradesco_Varejo"). Extraído para cá porque o mesmo padrão de título numerado /
 * subtítulo / parágrafo / lista se repete em todas as seções de 2 a 20.
 *
 * Cor dos títulos: o PDF-modelo real usa, nos três níveis de título, um azul petróleo
 * escuro (`HEADING_COLOR`, #0F4761) em vez do vermelho da marca — confirmado por inspeção
 * pixel-a-pixel (RGB(15,71,97) constante em várias páginas). Uma revisão anterior deste PR
 * trocou essa cor para o vermelho da marca a pedido pontual da Julia (PM); revisando o
 * modelo novamente, ela confirmou priorizar o que o PDF-modelo real mostra sempre que
 * houver conflito — por isso o título volta a usar `HEADING_COLOR`, ver `_helpers/brand.js`
 * para o histórico completo. O corpo do texto (Paragraph/Bullets) não define cor própria —
 * usa a cor base do documento (preta, ver `PageContent.js`), como no modelo.
 */

// Título de seção numerada (nível 1), ex.: "4. DIREITOS E DEVERES...".
export const SectionHeading = ({ numero, children }) => (
  <>
    <Text bold size="small" color={HEADING_COLOR}>
      {numero}. {children}
    </Text>
    <Separator weight={8} />
  </>
)

// Subtítulo nível 2 (####), ex.: "4.1. Responsabilidades do Empregador". `id` opcional —
// usado pelo Sumário (`00b.SumarioSection.js`) como âncora de link E como marcador de
// medição de paginação real (ver `_helpers/pagination.js`); sem ele o subtítulo continua
// funcionando normalmente, só não aparece linkável/numerado no Sumário.
export const SubHeading = ({ id, children }) => (
  <>
    <Separator weight={6} />
    <Text id={id} bold size="small" color={HEADING_COLOR}>
      {children}
    </Text>
    <Separator weight={5} />
  </>
)

// Subtítulo nível 3 (#####), ex.: "4.1.1. Medidas contra o assédio...". No modelo real
// este nível usa o mesmo azul petróleo em negrito, sem itálico (não o itálico/semibold
// preto usado antes, que era uma suposição de estilo institucional genérico).
export const SubSubHeading = ({ children }) => (
  <>
    <Separator weight={5} />
    <Text bold size="small" color={HEADING_COLOR}>
      {children}
    </Text>
    <Separator weight={4} />
  </>
)

// Parágrafo de corpo de texto.
export const Paragraph = ({ children }) => (
  <>
    <Text size="p" align="justify">
      {children}
    </Text>
    <Separator weight={6} />
  </>
)

// Lista com marcadores. Cada item pode ser texto simples ou um nó (para permitir
// sub-listas aninhadas, ex.: itens "I., II., III." dentro de uma alínea "b)").
export const Bullets = ({ items }) => (
  <>
    <List>
      {items.map((item, index) => (
        <ListItem key={index}>
          <Text size="p" align="justify">
            {item}
          </Text>
        </ListItem>
      ))}
    </List>
    <Separator weight={4} />
  </>
)
